import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MvsCrudService} from '../../../../core/shared/service/crud/mvs-crud.service';
import {
    BulkAssignRequest,
    BulkDoNotContactRequest,
    BulkOperationByFilterRequest,
    BulkOperationItemResponse,
    BulkOperationResponse,
    BulkOperationSummary,
    BulkRemovalRequest,
    BulkStatusChangeRequest
} from '../../model/dto/api/bulk-operation.interface';

/**
 * Service for Campaign Bulk Operations functionality (F-019).
 * Provides methods for:
 * - Bulk status change
 * - Bulk assignment
 * - Bulk removal
 * - Bulk do-not-contact marking
 * - Operations by filter criteria
 * - Operation status and history
 */
@Injectable({
    providedIn: 'root'
})
export class CampaignBulkOperationService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/campaignBulkOperations');
    }

    getObjectIcon(): string {
        return 'fa-solid fa-list-check';
    }

    /**
     * Creates a bulk status change operation for selected members.
     * @param campaignId The campaign ID
     * @param request The bulk status change request
     * @returns Observable of the operation result
     */
    bulkStatusChange(campaignId: number, request: BulkStatusChangeRequest): Observable<BulkOperationResponse> {
        return this.http.post<BulkOperationResponse>(`${this.apiUrl}/campaigns/${campaignId}/bulk-status-change`, request);
    }

    /**
     * Creates a bulk assignment operation for selected members.
     * @param campaignId The campaign ID
     * @param request The bulk assign request
     * @returns Observable of the operation result
     */
    bulkAssign(campaignId: number, request: BulkAssignRequest): Observable<BulkOperationResponse> {
        return this.http.post<BulkOperationResponse>(`${this.apiUrl}/campaigns/${campaignId}/bulk-assign`, request);
    }

    /**
     * Creates a bulk removal operation for selected members.
     * @param campaignId The campaign ID
     * @param request The bulk removal request
     * @returns Observable of the operation result
     */
    bulkRemove(campaignId: number, request: BulkRemovalRequest): Observable<BulkOperationResponse> {
        return this.http.post<BulkOperationResponse>(`${this.apiUrl}/campaigns/${campaignId}/bulk-remove`, request);
    }

    /**
     * Creates a bulk do-not-contact operation for selected members.
     * @param campaignId The campaign ID
     * @param request The bulk DNC request
     * @returns Observable of the operation result
     */
    bulkDoNotContact(campaignId: number, request: BulkDoNotContactRequest): Observable<BulkOperationResponse> {
        return this.http.post<BulkOperationResponse>(`${this.apiUrl}/campaigns/${campaignId}/bulk-dnc`, request);
    }

    /**
     * Creates a bulk operation using filter criteria instead of explicit member IDs.
     * @param campaignId The campaign ID
     * @param request The bulk operation by filter request
     * @returns Observable of the operation result
     */
    bulkOperationByFilter(campaignId: number, request: BulkOperationByFilterRequest): Observable<BulkOperationResponse> {
        return this.http.post<BulkOperationResponse>(`${this.apiUrl}/campaigns/${campaignId}/bulk-by-filter`, request);
    }

    /**
     * Gets all bulk operations for a campaign.
     * @param campaignId The campaign ID
     * @returns Observable of the list of operations
     */
    getOperationsForCampaign(campaignId: number): Observable<BulkOperationResponse[]> {
        return this.http.get<BulkOperationResponse[]>(`${this.apiUrl}/campaigns/${campaignId}`);
    }

    /**
     * Gets a specific bulk operation by ID.
     * @param operationId The operation ID
     * @returns Observable of the operation
     */
    getOperation(operationId: number): Observable<BulkOperationResponse> {
        return this.http.get<BulkOperationResponse>(`${this.apiUrl}/${operationId}`);
    }

    /**
     * Gets the items for a bulk operation.
     * @param operationId The operation ID
     * @returns Observable of the list of operation items
     */
    getOperationItems(operationId: number): Observable<BulkOperationItemResponse[]> {
        return this.http.get<BulkOperationItemResponse[]>(`${this.apiUrl}/${operationId}/items`);
    }

    /**
     * Gets only the failed items for a bulk operation.
     * @param operationId The operation ID
     * @returns Observable of the list of failed operation items
     */
    getFailedItems(operationId: number): Observable<BulkOperationItemResponse[]> {
        return this.http.get<BulkOperationItemResponse[]>(`${this.apiUrl}/${operationId}/failed-items`);
    }

    /**
     * Gets a summary of a bulk operation.
     * @param operationId The operation ID
     * @returns Observable of the operation summary
     */
    getOperationSummary(operationId: number): Observable<BulkOperationSummary> {
        return this.http.get<BulkOperationSummary>(`${this.apiUrl}/${operationId}/summary`);
    }

    /**
     * Cancels a pending bulk operation.
     * @param operationId The operation ID
     * @returns Observable of the cancelled operation
     */
    cancelOperation(operationId: number): Observable<BulkOperationResponse> {
        return this.http.post<BulkOperationResponse>(`${this.apiUrl}/${operationId}/cancel`, {});
    }
}
